import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { ChevronRight, ShieldAlert, AlertTriangle, Thermometer } from "lucide-react";
import SignatureCanvas from "react-signature-canvas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QAOverridePage() {
  const { transferId } = useParams();
  const navigate = useNavigate();
  const [load, setLoad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refData, setRefData] = useState(null);
  const [saving, setSaving] = useState(false);
  const sigRef = useRef(null);

  // Form state
  const [acknowledged, setAcknowledged] = useState([]);
  const [notes, setNotes] = useState("");
  const [corrective, setCorrective] = useState("");
  const [overrideBy, setOverrideBy] = useState("John Doe");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [loadRes, refRes] = await Promise.all([
          axios.get(`${API}/internal-transfers/${transferId}`),
          axios.get(`${API}/internal-transfers/reference-data`),
        ]);
        setLoad(loadRes.data);
        setRefData(refRes.data);
        if (refRes.data?.users?.[0]) setOverrideBy(refRes.data.users[0]);
      } catch { toast.error("Failed to load"); navigate(`/internal-transfers/${transferId}`); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [transferId, navigate]);

  const toggleAck = (item) => setAcknowledged(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);

  const handleSubmit = async () => {
    const failedItems = load?.inspection?.failed_items || [];
    if (acknowledged.length < failedItems.length) { toast.error(`Acknowledge all ${failedItems.length} failure items before overriding`); return; }
    if (!notes.trim()) { toast.error("Override notes are required"); return; }
    let sig = null;
    if (sigRef.current && !sigRef.current.isEmpty()) sig = sigRef.current.toDataURL("image/png");

    setSaving(true);
    try {
      await axios.post(`${API}/internal-transfers/${load.truck_load_id}/qa-override`, {
        notes, override_by: overrideBy, failure_acknowledgements: acknowledged,
        corrective_actions: corrective, signature: sig,
      });
      toast.success("QA Override applied — Ready to Unload");
      navigate(`/internal-transfers/${transferId}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-[calc(100vh-60px)]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-600" /></div>;
  if (!load) return null;

  const insp = load.inspection || {};
  const failedItems = insp.failed_items || [];
  const users = refData?.users || [];
  const allAcked = acknowledged.length >= failedItems.length;

  return (
    <div className="flex flex-col h-[calc(100vh-60px)]" data-testid="qa-override-page">
      {/* Breadcrumb */}
      <div className="px-6 py-2 border-b border-zinc-200 bg-white">
        <nav className="flex items-center gap-1.5 text-xs text-zinc-500">
          <button onClick={() => navigate("/internal-transfers")} className="hover:text-zinc-800">Internal Transfers</button>
          <ChevronRight className="w-3 h-3" />
          <button onClick={() => navigate(`/internal-transfers/${transferId}`)} className="hover:text-zinc-800">{load.to_number}</button>
          <ChevronRight className="w-3 h-3" />
          <span className="text-zinc-800 font-medium">QA Override</span>
        </nav>
      </div>

      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-200 bg-orange-50">
        <div className="flex items-center gap-3">
          <ShieldAlert className="w-6 h-6 text-orange-600" />
          <div>
            <h1 className="text-lg font-bold text-zinc-900">QA Override — {load.to_number}</h1>
            <p className="text-xs text-zinc-600">{load.description}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Inspection Results Summary */}
          <Card className="border-red-200">
            <CardHeader className="pb-3"><CardTitle className="text-base text-red-800 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Failed Inspection Results</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-zinc-500">Inspector:</span> <span className="font-medium">{insp.inspected_by}</span></div>
                <div><span className="text-zinc-500">Date:</span> <span className="font-medium">{insp.timestamp?.slice(0, 19)}</span></div>
                <div><span className="text-zinc-500">Reefer Temp:</span> <span className="font-medium">{insp.reefer_temp}°F</span></div>
                <div><span className="text-zinc-500">Result:</span> <span className="font-bold text-red-700">FAIL</span></div>
              </div>
              {insp.inspector_notes && <div className="text-xs bg-red-100 rounded p-2 text-red-800">Inspector Notes: {insp.inspector_notes}</div>}
            </CardContent>
          </Card>

          {/* Failure Items — must all be acknowledged */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Failure Items — Acknowledge Each ({acknowledged.length}/{failedItems.length})</CardTitle></CardHeader>
            <CardContent>
              {failedItems.length === 0 ? (
                <div className="text-xs text-zinc-400 text-center py-4">No specific failure items recorded</div>
              ) : (
                <div className="space-y-3">
                  {failedItems.map((item, i) => (
                    <label key={i} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${acknowledged.includes(item) ? "bg-orange-50 border-orange-300" : "bg-white border-zinc-200 hover:bg-zinc-50"}`}>
                      <Checkbox checked={acknowledged.includes(item)} onCheckedChange={() => toggleAck(item)} className="mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-zinc-900">{item}</div>
                        <div className="text-[11px] text-zinc-500 mt-0.5">Check to acknowledge this failure item has been reviewed</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Override Details */}
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Override Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs">Corrective Actions Taken</Label>
                <Textarea value={corrective} onChange={e => setCorrective(e.target.value)} className="mt-1 text-sm" rows={3} placeholder="Describe what corrective actions were taken..." data-testid="corrective-actions" />
              </div>
              <div>
                <Label className="text-xs">Override Notes * (Required)</Label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} className="mt-1 text-sm" rows={3} placeholder="Reason for QA override..." data-testid="override-notes" />
              </div>
              <div>
                <Label className="text-xs">QA Override By</Label>
                <Select value={overrideBy} onValueChange={setOverrideBy}>
                  <SelectTrigger className="h-9 text-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{users.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">QA Signature</Label>
                <div className="border border-zinc-300 rounded mt-1 bg-white"><SignatureCanvas ref={sigRef} canvasProps={{ className: "w-full h-24" }} /></div>
                <button onClick={() => sigRef.current?.clear()} className="text-[10px] text-zinc-500 mt-0.5">Clear</button>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-between items-center pt-2 pb-8">
            <Button variant="outline" onClick={() => navigate(`/internal-transfers/${transferId}`)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving || !allAcked || !notes.trim()} className="bg-orange-600 hover:bg-orange-700" data-testid="submit-override-btn">
              <ShieldAlert className="w-4 h-4 mr-2" /> {saving ? "Submitting..." : "Submit QA Override"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
